import React, { useState, useEffect, useRef, useCallback, useContext, Suspense, lazy } from "react";
import { Spinner } from "@radix-ui/themes";
import Inventory from "./Inventory";
import Committed from "./Committed";
import Cash from "./Cash";
import { AddEditCommitted, AddEditInventory } from "./lazyForms";
import Credit from "./Credit";
import CreditCleared from "./CreditCleared";
import Cancelled from "./Cancelled";
import Customers from "./Customers";
import Sellers from "./Sellers";
import Brokers from "./Brokers";
import { emitter, normalizeRecords } from "./UtilityFunctions";
import useSheetData from "./hooks/useSheetData.js";
import useSlidingIndicator from "./hooks/useSlidingIndicator";
import { apiFetch } from "./api.js";
import fields from "./fields";
import { GlobalContext } from "./GlobalContext";
import { salesSubTabs, canSeeSalesSubTab } from "./salesSubTabs";
import ErrorBoundary from "./ErrorBoundary";
//Heavy sections load on demand (plan 5.1) — they also pull the chart and
//export libraries (recharts, xlsx, jspdf) out of the entry chunk.
const Settings = lazy(() => import("./Settings"));
const ReportGenerator = lazy(() => import("./ReportGenerator"));
const PaymentInsights = lazy(() => import("./PaymentInsights"));
const RecycleBin = lazy(() => import("./RecycleBin"));
const Dashboard = lazy(() => import("./Dashboard"));
//Lazy like the other whole-page tabs: the composer pulls in the template editor
//and its variable machinery, none of which the daily sheets need.
const Messages = lazy(() => import("./Messages"));
const EnquiryList = lazy(() => import("./EnquiryList"));
import useTabJoyride, { useTabTourRegistrations } from './useTabJoyride';
import JoyridePortal from './JoyridePortal';
import CustomTooltip from './JoyrideCustomTooltip';
import { themeAccent } from './theme';

//Module scope: useSheetData requires a referentially-stable transformRecord
const customerTransform = (row) => ({
    ...row,
    CustomerName: row.IsCompany ? row.CompanyName : `${row.FirstName} ${row.LastName}`
});

//Find the row a notification refers to across the loaded sheet datasets.
//Sale rows match on DealAgreementNo, vehicles on VehicleID. The sheetHint is
//tried first (a vehicle can also live on other sheets, e.g. after a sale).
function findNotificationRecord(data, { dealAgreementNo, vehicleId, sheetHint }) {
    const order = ['Committed', 'Credit', 'Cash', 'CreditCleared', 'Inventory'];
    if (sheetHint && order.includes(sheetHint)) {
        order.splice(order.indexOf(sheetHint), 1);
        order.unshift(sheetHint);
    }
    for (const sheet of order) {
        const rows = data[sheet];
        if (!Array.isArray(rows)) continue;
        let row = null;
        if (dealAgreementNo != null && sheet !== 'Inventory') {
            row = rows.find((r) => r.DealAgreementNo === dealAgreementNo);
        }
        if (!row && vehicleId != null) {
            row = rows.find((r) => r.VehicleID === vehicleId);
        }
        if (row) return { sheet, row };
    }
    return null;
}

//Resolve a record to { sheet, row }, cache first and server second. Throws when
//it cannot be resolved at all, so each caller decides how loud to be about it.
async function resolveRecord(cache, target) {
    const cached = findNotificationRecord(cache, target);
    if (cached) return cached;

    const sheet = target.sheetHint;
    if (!sheet) throw new Error("No sheet was given for that record.");

    const res = await apiFetch("/search/vehicle", {
        body: {
            sheet,
            VehicleID: target.vehicleId,
            DealAgreementNo: target.dealAgreementNo,
        }
    });
    const payload = await res.json();
    if (!res.ok || !payload.ok || !payload.row) {
        throw new Error(payload.message || `HTTP ${res.status}`);
    }
    //Must match how useSheetData normalizes fetched rows, or the modal's
    //dirty-tracking sees every field as changed on open (DECIMALs arrive as
    //strings, NULL numerics as null not "").
    return { sheet, row: normalizeRecords([payload.row], fields)[0] };
}


function SubNav({ activeTab = "tab-inventory", activeSubMenu, switchActiveSubMenu, logInStatus, setLogInStatus, disableTabsForTour, setDisableTabsForTour }) {
    const globals = useContext(GlobalContext);
    const { socket, toggleMessageBox } = globals;

    const [pendingSelection, setPendingSelection] = useState("");
    //{ type, ids } handed over by "Message selected" on a CRM sheet.
    const [pendingBroadcast, setPendingBroadcast] = useState(null);
    //Modal opened by clicking a notification (resolved from the loaded sheet data).
    const [notifModal, setNotifModal] = useState(null);
    const dataRef = useRef({});

    useEffect(() => {
        const handler = ({ selectedId, broadcastSelection }) => {
            if (selectedId) setPendingSelection(selectedId);
            //Rows ticked on a CRM sheet, on their way to the broadcast composer.
            if (broadcastSelection) setPendingBroadcast(broadcastSelection);
        };
        emitter.on("navigate", handler);
        return () => emitter.removeListener("navigate", handler);
    }, []);

    //Opens the record's modal on its own tab. Both entry points below end here.
    const openResolved = useCallback((found, { initialTab = 1, highlight = false } = {}) => {
        const isInventory = found.sheet === "Inventory";
        emitter.emit("navigate", {
            tab: isInventory ? "tab-inventory" : "tab-sales",
            subMenu: found.sheet,
            //Highlights the row on arrival. A server-fetched row on a paginated
            //sheet isn't on the current page, so this no-ops there.
            ...(highlight && {
                selectedId: isInventory ? found.row.VehicleID : found.row.DealAgreementNo,
            }),
        });
        setNotifModal({
            kind: isInventory ? "inventory" : "sale",
            car: found.row,
            parentSheetName: found.sheet,
            initialTab,
        });
    }, []);

    //Clicking a notification: find its record across the loaded sheets, switch
    //to that sheet, and open the record's modal on the relevant tab. The cache
    //is a shortcut, not the source of truth — Cash/CreditCleared/Cancelled are
    //server-paginated so an older deal is never in it, and since the sheets are
    //now fetched per-tab the cache is empty for anything the user has not
    //opened yet. Both paths therefore fall back to the same server lookup that
    //header search already used; before, a notification that missed the cache
    //silently navigated to the sheet and opened nothing.
    useEffect(() => {
        const handler = async (target) => {
            try {
                const found = await resolveRecord(dataRef.current, target);
                openResolved(found, { initialTab: target.tab || 1 });
            } catch {
                //Best-effort: a notification is ambient, so a failed lookup
                //drops the user on the right sheet rather than raising a dialog.
                if (target.sheetHint) emitter.emit("navigate", {
                    tab: target.sheetHint === "Inventory" ? "tab-inventory" : "tab-sales",
                    subMenu: target.sheetHint,
                });
            }
        };
        emitter.on("open-notification", handler);
        return () => emitter.removeListener("open-notification", handler);
    }, [openResolved]);

    //Header search result: the user asked for this specific vehicle, so unlike
    //the notification path a failure is worth interrupting them for.
    useEffect(() => {
        const handler = async ({ sheet, VehicleID, DealAgreementNo }) => {
            try {
                const found = await resolveRecord(dataRef.current, {
                    dealAgreementNo: DealAgreementNo,
                    vehicleId: VehicleID,
                    sheetHint: sheet,
                });
                openResolved(found, { highlight: true });
            } catch (err) {
                toggleMessageBox({
                    on: true,
                    title: "Error",
                    message: `Could not open that vehicle. ${err.message}`,
                    buttons: ["OK"]
                });
            }
        };
        emitter.on("open-vehicle", handler);
        return () => emitter.removeListener("open-vehicle", handler);
    }, [toggleMessageBox, openResolved]);

    const [creditClearedPagination, setCreditClearedPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalRecords: 50,
        recordsPerPage: 50,
        hasNextPage: false,
        hasPrevPage: false,
        recordsOnCurrentPage: 50,
        searchTerm: "",
        dateFrom: "",
        dateTo: ""
    });

    const [cashPagination, setCashPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalRecords: 50,
        recordsPerPage: 50,
        hasNextPage: false,
        hasPrevPage: false,
        recordsOnCurrentPage: 50,
        searchTerm: "",
        dateFrom: "",
        dateTo: ""
    });

    const [cancelledPagination, setCancelledPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalRecords: 50,
        recordsPerPage: 50,
        hasNextPage: false,
        hasPrevPage: false,
        recordsOnCurrentPage: 50,
        searchTerm: "",
        dateFrom: "",
        dateTo: ""
    });

    const [brokersPagination, setBrokersPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalRecords: 50,
        recordsPerPage: 50,
        hasNextPage: false,
        hasPrevPage: false,
        recordsOnCurrentPage: 50,
        searchTerm: "",
    });

    const [customersPagination, setCustomersPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalRecords: 50,
        recordsPerPage: 50,
        hasNextPage: false,
        hasPrevPage: false,
        recordsOnCurrentPage: 50,
        searchTerm: "",
    });

    const [sellersPagination, setSellersPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalRecords: 50,
        recordsPerPage: 50,
        hasNextPage: false,
        hasPrevPage: false,
        recordsOnCurrentPage: 50,
        searchTerm: "",
    });

    const { 
        runTour, 
        steps, 
        stepIndex, 
        handleJoyrideCallback,
        handleStartTutorial
    } = useTabJoyride(
        activeTab,
        null,
        activeSubMenu,
        switchActiveSubMenu,
        disableTabsForTour,
        setDisableTabsForTour
    )

    //One hook serves every tab tour. The list of which tabs have one lives with
    //the steps in useTabJoyride, so adding a tab tour is one edit there rather
    //than three spread across two files.
    useTabTourRegistrations(activeTab, handleStartTutorial);


    //All nine sheets live on react-query (plan step 4.8) — fetching, socket
    //patching and page-change refetches are useSheetData's job.
    //
    //Each is gated on the screen that renders it. SubNav is mounted for every
    //tab (CarsNav.jsx), so ungated these fired nine concurrent POSTs the moment
    //a user logged in — several MB of JSON for a large tenant, before they had
    //clicked anything, and most of it for sheets they would never open. The
    //gate is on the fetch only: once a sheet has been visited its cache stays
    //subscribed to socket patches, so coming back to it is instant and current.
    const onSales = activeTab === "tab-sales";
    //One gate for both halves of a sheet: whether it is fetched and whether it
    //is rendered. The permission belongs here rather than only on the tab strip
    //— a stale activeSubMenu survives a role change, and "navigate" events name
    //a sub-menu directly — and a fetch the server will refuse is a toast the
    //user can do nothing about.
    const salesSheet = (name) =>
        onSales && activeSubMenu === name && canSeeSalesSubTab(name, globals.can);

    const { data: inventory, setData: setInventory, isLoading: inventoryLoading } = useSheetData("Inventory", "/getinventory", {
        enabled: activeTab === "tab-inventory" && activeSubMenu === "Inventory"
    });
    const { data: committed, setData: setCommitted, isLoading: committedLoading } = useSheetData("Committed", "/getcommitted", {
        enabled: salesSheet("Committed")
    });
    const { data: creditCleared, setData: setCreditCleared, isLoading: creditClearedLoading } = useSheetData("CreditCleared", "/getcreditcleared", {
        pagination: creditClearedPagination,
        setPagination: setCreditClearedPagination,
        enabled: salesSheet("CreditCleared")
    });
    const { data: cash, setData: setCash, isLoading: cashLoading } = useSheetData("Cash", "/getcash", {
        pagination: cashPagination,
        setPagination: setCashPagination,
        enabled: salesSheet("Cash")
    });
    //Cancelled deals: read-only historical viewer (DELETE_FEATURE_PLAN 4.2).
    //Same paginated shape/gate (anyAuthenticated) as /getcash.
    const { data: cancelled, setData: setCancelled, isLoading: cancelledLoading } = useSheetData("Cancelled", "/getcancelled", {
        pagination: cancelledPagination,
        setPagination: setCancelledPagination,
        enabled: salesSheet("Cancelled")
    });
    const { data: customers, setData: setCustomers, isLoading: customersLoading } = useSheetData("Customers", "/getcustomers", {
        pagination: customersPagination,
        setPagination: setCustomersPagination,
        transformRecord: customerTransform,
        enabled: activeTab === "tab-customers"
    });
    const { data: brokers, setData: setBrokers, isLoading: brokersLoading } = useSheetData("Brokers", "/getbrokers", {
        pagination: brokersPagination,
        setPagination: setBrokersPagination,
        enabled: activeTab === "tab-brokers"
    });
    const { data: sellers, setData: setSellers, isLoading: sellersLoading } = useSheetData("Sellers", "/getsellers", {
        pagination: sellersPagination,
        setPagination: setSellersPagination,
        enabled: activeTab === "tab-sellers"
    });
    //Unpaginated sheet: the server returns all rows, so no pagination options
    const { data: credit, setData: setCredit, isLoading: creditLoading } = useSheetData("Credit", "/getcredit", {
        enabled: salesSheet("Credit")
    });

    //Latest datasets for the record resolver (the handlers are registered once).
    //With the gates above these are usually empty for the sheets the user is not
    //looking at, which is exactly why resolveRecord falls back to the server.
    dataRef.current = { Inventory: inventory, Committed: committed, Credit: credit, Cash: cash, CreditCleared: creditCleared };

    //A 'permission_denied' listener used to sit here, described as "the cross-sheet
    //permission dialog". The backend has never emitted that event — the only two
    //references in the tree were this handler and its own cleanup, so the dialog
    //could not fire. Refusals actually arrive as update_record with ok:false, and
    //hooks/useSheetData.js now surfaces them on the sheet that raised the edit.

    useEffect(() => {
        if (!logInStatus) return; // Return when it changes between "login" and "register"
        if (!socket) return;
        //Initial fetches are react-query's job now — just bring the socket up
        if (!socket.connected) socket.connect();
    }, [logInStatus, socket]);

    return (
        <>
            <div className="subnav">
                <Suspense fallback={
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
                        <Spinner size="3" />
                    </div>
                }>
                {(() => { // Using an IIFE for switch inside JSX
                    switch (activeTab) {
                        case "tab-dashboard":
                            return (
                                <ErrorBoundary label="Dashboard">
                                <>
                                    <JoyridePortal
                                        steps={steps}
                                        stepIndex={stepIndex}
                                        run={runTour}
                                        callback={handleJoyrideCallback}
                                        continuous
                                        showProgress
                                        showSkipButton
                                        disableCloseOnEsc={true}
                                        disableOverlayClose={true}
                                        locale={{
                                            back: 'Back',
                                            close: 'Close',
                                            last: 'Finish',
                                            next: 'Next',
                                            skip: 'Skip',
                                        }}
                                        spotlightPadding={3}
                                        spotlightClicks={false}
                                        styles={{
                                        options: {
                                            primaryColor: themeAccent(),
                                            zIndex: 10000,
                                        },
                                        }}
                                        tooltipComponent={CustomTooltip}
                                    />                                
                                    <Dashboard />
                                </>
                                </ErrorBoundary>
                            );
                        case "tab-inventory":
                            return (
                                <ErrorBoundary label="Inventory">
                                <>
                                    <JoyridePortal
                                        steps={steps}
                                        stepIndex={stepIndex} 
                                        run={runTour}
                                        callback={handleJoyrideCallback}
                                        continuous
                                        showProgress
                                        showSkipButton
                                        disableCloseOnEsc={true}
                                        disableOverlayClose={true}
                                        locale={{
                                            back: 'Back',
                                            close: 'Close',
                                            last: 'Finish',
                                            next: 'Next',
                                            skip: 'Skip',
                                        }}
                                        spotlightPadding={3}
                                        spotlightClicks={false}
                                        styles={{
                                        options: {
                                            primaryColor: themeAccent(),
                                            zIndex: 10000,
                                        },
                                        }}
                                        tooltipComponent={CustomTooltip}
                                    />                                
                                    <SubMenuTabsInventory activeSubMenu={activeSubMenu} switchActiveSubMenu={switchActiveSubMenu} />
                                    {activeSubMenu === "Inventory" && (
                                        <Inventory cars={inventory} setCars={setInventory} isLoading={inventoryLoading} initialSelectedRow={pendingSelection} />
                                    )}
                                    {activeSubMenu === "InventoryReport" && "Inventory Report"}
                                </>
                                </ErrorBoundary>
                            );
                        case "tab-sales":
                            // Select appropriate sub-menu based on activeSubMenu
                            return (
                                <ErrorBoundary label="Sales">
                                <>
                                    <JoyridePortal
                                        steps={steps}
                                        stepIndex={stepIndex} 
                                        run={runTour}
                                        callback={handleJoyrideCallback}
                                        continuous
                                        showProgress
                                        showSkipButton
                                        disableCloseOnEsc={true}
                                        disableOverlayClose={true}
                                        locale={{
                                            back: 'Back',
                                            close: 'Close',
                                            last: 'Finish',
                                            next: 'Next',
                                            skip: 'Skip',
                                        }}
                                        spotlightPadding={3}
                                        spotlightClicks={false}
                                        styles={{
                                        options: {
                                            primaryColor: themeAccent(),
                                            zIndex: 10000,
                                        },
                                        }}
                                        tooltipComponent={CustomTooltip}
                                    />
                                    <SubMenuTabsSales activeSubMenu={activeSubMenu} switchActiveSubMenu={switchActiveSubMenu} can={globals.can} />
                                    {salesSheet("Committed") && (
                                        <Committed
                                    isLoading={committedLoading}
                                            id="committed"
                                            cars={committed}
                                            setCars={setCommitted}
                                            initialSelectedRow={pendingSelection}
                                        />
                                    )}
                                    {salesSheet("Cash") && (
                                        <Cash
                                    isLoading={cashLoading}
                                            id="cash"
                                            cars={cash}
                                            setCars={setCash}
                                            pagination={cashPagination}
                                            setPagination={setCashPagination}
                                            initialSelectedRow={pendingSelection}
                                        />
                                    )}
                                    {salesSheet("Credit") && (
                                        <Credit
                                    isLoading={creditLoading}
                                            id="credit"
                                            cars={credit}
                                            setCars={setCredit}
                                            initialSelectedRow={pendingSelection}
                                        />
                                    )}
                                    {salesSheet("CreditCleared") && (
                                        <CreditCleared
                                    isLoading={creditClearedLoading}
                                            id="creditcleared"
                                            cars={creditCleared}
                                            setCars={setCreditCleared}
                                            pagination={creditClearedPagination}
                                            setPagination={setCreditClearedPagination}
                                            initialSelectedRow={pendingSelection}
                                        />
                                    )}
                                    {salesSheet("Cancelled") && (
                                        <Cancelled
                                    isLoading={cancelledLoading}
                                            id="cancelled"
                                            cars={cancelled}
                                            setCars={setCancelled}
                                            pagination={cancelledPagination}
                                            setPagination={setCancelledPagination}
                                            initialSelectedRow={pendingSelection}
                                        />
                                    )}
                                </>
                                </ErrorBoundary>
                            );
                        case "tab-logistics":
                            return (
                                <div>
                                    LOGISTICS
                                </div>
                            );
                        case "tab-customers":
                            return (
                                <ErrorBoundary label="Customers">
                                <Customers
                                    isLoading={customersLoading}
                                    customers={customers}
                                    setCustomers={setCustomers}
                                    pagination={customersPagination}
                                    setPagination={setCustomersPagination}
                                />
                                </ErrorBoundary>
                            );
                        case "tab-brokers":
                            return (
                                <ErrorBoundary label="Brokers">
                                <Brokers
                                    isLoading={brokersLoading}
                                    brokers={brokers}
                                    setBrokers={setBrokers}
                                    pagination={brokersPagination}
                                    setPagination={setBrokersPagination}
                                />
                                </ErrorBoundary>
                            );
                        case "tab-sellers":
                            return (
                                <ErrorBoundary label="Sellers">
                                <Sellers
                                    isLoading={sellersLoading}
                                    sellers={sellers}
                                    setSellers={setSellers}
                                    pagination={sellersPagination}
                                    setPagination={setSellersPagination}
                                />
                                </ErrorBoundary>
                            );
                        case "tab-messages":
                            return (
                                <ErrorBoundary label="Messages">
                                <>
                                    <SubMenuTabsMessages activeSubMenu={activeSubMenu} switchActiveSubMenu={switchActiveSubMenu} />
                                    <Messages
                                        activeSubMenu={activeSubMenu}
                                        switchActiveSubMenu={switchActiveSubMenu}
                                        initialSelection={pendingBroadcast}
                                        onSelectionConsumed={() => setPendingBroadcast(null)}
                                    />
                                </>
                                </ErrorBoundary>
                            );
                        case "tab-enquiries":
                            //The global inbox. The same component renders inside the
                            //vehicle modal with a vehicleId; without one it lists every
                            //enquiry across the dealership, which is the view a
                            //salesperson actually wants each morning.
                            return (
                                <ErrorBoundary label="Enquiries">
                                <div>
                                    <EnquiryList />
                                </div>
                                </ErrorBoundary>
                            );
                        case "tab-settings":
                            return (
                                <ErrorBoundary label="Settings">
                                <div>
                                    <Settings />
                                </div>
                                </ErrorBoundary>
                            );
                        case "tab-payments":
                            return (
                                <ErrorBoundary label="Payments">
                                <div>
                                    <PaymentInsights dealershipId={globals.DealershipID} />
                                </div>
                                </ErrorBoundary>
                            );
                        case "tab-reports":
                            return (
                                <ErrorBoundary label="Reports">
                                <div>
                                    <ReportGenerator dealershipId={globals.DealershipID} />
                                </div>
                                </ErrorBoundary>
                            );
                        case "tab-recyclebin":
                            return (
                                <ErrorBoundary label="Recycle Bin">
                                <div>
                                    <RecycleBin />
                                </div>
                                </ErrorBoundary>
                            );
                        default:
                            return null;
                    }
                })()}
                </Suspense>
            </div>

            {/* Modal opened by clicking a notification (see NotificationBell). */}
            {notifModal && notifModal.kind === "sale" && (
                <AddEditCommitted
                    formMode="edit"
                    showModal={() => setNotifModal(null)}
                    car={notifModal.car}
                    parentSheetName={notifModal.parentSheetName}
                    origin={notifModal.car.Origin}
                    initialTab={notifModal.initialTab}
                />
            )}
            {notifModal && notifModal.kind === "inventory" && (
                <AddEditInventory
                    formMode="edit"
                    showModal={() => setNotifModal(null)}
                    car={notifModal.car}
                    parentSheetName={notifModal.parentSheetName}
                    origin={notifModal.car.Origin}
                    initialTab={notifModal.initialTab}
                />
            )}
        </>
    );
}

//One sub-menu row. The button ids are contracts (tours and the QA suites
//target them), so they stay explicit per item rather than being derived.
function SubMenuRow({ id, items, activeSubMenu, switchActiveSubMenu }) {
    const rowRef = useRef(null);
    const indicator = useSlidingIndicator(rowRef, activeSubMenu, items.length);

    return (
        <div id={id} className="sub-menu" ref={rowRef}>
            {items.map((item) => (
                <button
                    key={item.key}
                    id={item.id}
                    data-navkey={item.key}
                    className={`sub-menu-tab${activeSubMenu === item.key ? " sub-menu-active" : ""}`}
                    aria-current={activeSubMenu === item.key ? "page" : undefined}
                    onClick={() => switchActiveSubMenu(item.key)}
                >
                    {item.label}
                </button>
            ))}
            <span
                className="tabbar-indicator"
                aria-hidden="true"
                style={{
                    transform: `translateX(${indicator.left}px)`,
                    width: indicator.width,
                    opacity: indicator.ready && indicator.width ? 1 : 0,
                }}
            />
        </div>
    );
}

const INVENTORY_SUBTABS = [{ key: "Inventory", label: "Inventory" }];

//History leads, and is the default in CarsNav, because that is what "Messages"
//is opened for most of the time — the segment allowance only stretches to a
//couple of broadcasts a month, while delivery status gets checked constantly.
//
//Compose here is the CAMPAIGN composer: one message to a chosen audience.
//Messaging about a specific deal still happens in the modal opened from that
//deal's row, which is where the balances and vehicle details it needs live.
const MESSAGES_SUBTABS = [
    { key: "History",   label: "History",   id: "sub-menu-history" },
    { key: "Compose",   label: "Compose",   id: "sub-menu-compose" },
    { key: "Templates", label: "Templates", id: "sub-menu-templates" },
];

function SubMenuTabsMessages({ activeSubMenu, switchActiveSubMenu }) {
    return (
        <SubMenuRow
            id="sub-menu-messages"
            items={MESSAGES_SUBTABS}
            activeSubMenu={activeSubMenu}
            switchActiveSubMenu={switchActiveSubMenu}
        />
    );
}

function SubMenuTabsInventory({ activeSubMenu, switchActiveSubMenu }) {
    return (
        <SubMenuRow
            items={INVENTORY_SUBTABS}
            activeSubMenu={activeSubMenu}
            switchActiveSubMenu={switchActiveSubMenu}
        />
    );
}

function SubMenuTabsSales({ activeSubMenu, switchActiveSubMenu, can }) {
    return (
        <SubMenuRow
            id="sub-menu-sales"
            items={salesSubTabs(can)}
            activeSubMenu={activeSubMenu}
            switchActiveSubMenu={switchActiveSubMenu}
        />
    );
}

export default SubNav;